<script setup lang="ts">
  import { VSelect } from "@krainovsd/vue-ui";
  import { VisArea, VisAxis, VisCrosshair, VisLine, VisTooltip, VisXYContainer } from "@unovis/vue";
  import { computed, onMounted, ref, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRoute } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import {
    TARGET_BY_VALUE,
    compareBeta,
    compareGamma,
    formatComparisonPeriod,
    loadTargetItems,
    useComparisonStore,
  } from "@/entities/comparison";
  import {
    DEFAULT_FILTERS,
    INDICATOR_OPTIONS,
    buildPreviousPeriodBody,
    buildRequestStatisticsBody,
    fetchReport,
    parseIndicatorValue,
  } from "@/entities/report";
  import type { PeriodType, ReportFilters, ReportResponse } from "@/entities/report";
  import Card from "@/components/atoms/Card.vue";
  import ChevronDown from "@/components/icons/ChevronDown.vue";
  import ChevronRight from "@/components/icons/ChevronRight.vue";

  const { t } = useI18n();
  const route = useRoute();
  const comparisonStore = useComparisonStore();

  // A/B posterior density colours (kept explicit so SVG stroke gets a literal).
  const COLOR_A = "#22c55e";
  const COLOR_B = "#3b82f6";

  // The four supported rate metrics, each defined as numerator/denominator over
  // the report's indicator fields. EPSMS (earn per SMS) is a money proxy.
  type MetricKey = "cr" | "ctr" | "arpc" | "dr";
  const METRIC_OPTIONS: {
    key: MetricKey;
    label: string;
    numerator: string;
    denominator: string;
    method: "beta" | "gamma";
  }[] = [
    { key: "cr", label: "CR", numerator: "actions", denominator: "clicks", method: "beta" },
    { key: "ctr", label: "CTR", numerator: "clicks", denominator: "sends", method: "beta" },
    {
      key: "arpc",
      label: "ARPC",
      numerator: "approvedSum",
      denominator: "delivers",
      method: "gamma",
    },
    { key: "dr", label: "DR", numerator: "delivers", denominator: "sends", method: "beta" },
  ];

  const metric = ref<MetricKey>("cr");
  const metricConfig = computed(
    () => METRIC_OPTIONS.find((option) => option.key === metric.value) ?? METRIC_OPTIONS[0],
  );

  // "beta" metrics are rates shown as percent / percentage points; "gamma"
  // metrics are value-per-unit shown as raw values.
  const isRateMetric = computed(() => metricConfig.value.method === "beta");

  const recordId = computed(() => Number(route.params.id));
  const record = computed(() => comparisonStore.get(recordId.value) ?? null);
  onMounted(() => void comparisonStore.load());

  // "Как это работает" explainer card toggle.
  const infoOpen = ref(false);

  const subtitle = computed(() => {
    const value = record.value;
    if (!value) return "";
    const versus =
      value.targetValueTwo ||
      t("page.bayesianCompare.previousPeriod", {}, { default: "Прошлый период" });
    return `${value.targetValueOne} vs ${versus} • ${formatComparisonPeriod(value.period)}`;
  });

  // Label for object B in the chart legend (falls back to "previous period").
  const versusLabel = computed(() => {
    const two = record.value?.targetValueTwo;
    if (two) return two;
    return t("page.bayesianCompare.previousPeriod", {}, { default: "Прошлый период" });
  });

  // --- Resolve target object ids (names → ids), gated like the factor page ---
  const targetItems = ref<{ id: number; name: string }[]>([]);
  const targetsLoaded = ref(false);
  watch(
    record,
    async (value) => {
      if (!value) return;
      targetsLoaded.value = false;
      try {
        targetItems.value = await loadTargetItems(TARGET_BY_VALUE[value.target]);
      } catch {
        targetItems.value = [];
      } finally {
        targetsLoaded.value = true;
      }
    },
    { immediate: true },
  );
  const idByName = computed(() => new Map(targetItems.value.map((item) => [item.name, item.id])));
  const idOne = computed(() =>
    record.value ? (idByName.value.get(record.value.targetValueOne) ?? null) : null,
  );
  const idTwo = computed(() =>
    record.value ? (idByName.value.get(record.value.targetValueTwo) ?? null) : null,
  );

  const baseFilters = computed<ReportFilters | null>(() => {
    if (!record.value) return null;
    return {
      ...DEFAULT_FILTERS,
      attribution: 1,
      period: record.value.period as PeriodType,
      // Group by the target's own dimension (e.g. campaigns for a campaign target).
      section: TARGET_BY_VALUE[record.value.target].section,
      indicators: INDICATOR_OPTIONS.map((option) => option.key),
    };
  });

  function withTarget(filters: ReportFilters, id: number | null): ReportFilters {
    if (id === null || !record.value) return filters;
    return { ...filters, [TARGET_BY_VALUE[record.value.target].filterField]: [id] };
  }

  const bodyA = computed(() => {
    if (!baseFilters.value || !targetsLoaded.value) return null;
    return buildRequestStatisticsBody(withTarget(baseFilters.value, idOne.value));
  });
  const bodyB = computed(() => {
    if (!baseFilters.value || !record.value || !targetsLoaded.value) return null;
    if (record.value.mode === "previous_period") {
      return bodyA.value ? buildPreviousPeriodBody(bodyA.value) : null;
    }
    return buildRequestStatisticsBody(withTarget(baseFilters.value, idTwo.value));
  });

  const { data: dataA, isLoading: loadingA } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: computed(() => (bodyA.value ? `bayes-a-${JSON.stringify(bodyA.value)}` : "")),
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      if (!bodyA.value) throw new Error("missing A request body");
      return fetchReport(bodyA.value);
    },
  });
  const { data: dataB, isLoading: loadingB } = useCachedResource<ReportResponse>({
    namespace: CACHE_NAMESPACE.Report,
    key: computed(() => (bodyB.value ? `bayes-b-${JSON.stringify(bodyB.value)}` : "")),
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => {
      if (!bodyB.value) throw new Error("missing B request body");
      return fetchReport(bodyB.value);
    },
  });

  const isLoading = computed(
    () => (loadingA.value && !dataA.value) || (loadingB.value && !dataB.value),
  );

  // Sum an indicator field across all grouping rows of a report response.
  function sumField(response: ReportResponse | null, field: string): number {
    let total = 0;
    for (const { grouping } of response?.statistics ?? []) {
      for (const row of grouping ?? []) {
        total += parseIndicatorValue((row as Record<string, unknown>)[field]);
      }
    }
    return total;
  }

  // --- Bayesian result for the selected metric ----------------------------
  // A "beta" metric is a rate (Beta-Binomial); a "gamma" metric is a positive
  // continuous value-per-unit (Gamma-Poisson).
  const stats = computed(() => {
    if (!dataA.value || !dataB.value) return null;
    const cfg = metricConfig.value;
    const compare = cfg.method === "gamma" ? compareGamma : compareBeta;
    return compare(
      sumField(dataA.value, cfg.numerator),
      sumField(dataA.value, cfg.denominator),
      sumField(dataB.value, cfg.numerator),
      sumField(dataB.value, cfg.denominator),
    );
  });

  const probPercent = computed(() => (stats.value ? Math.round(stats.value.probAB * 100) : 0));
  const isBetter = computed(() => (stats.value ? stats.value.probAB >= 0.5 : true));
  const confidencePercent = computed(() => {
    if (!stats.value) return 0;
    return Math.round((isBetter.value ? stats.value.probAB : 1 - stats.value.probAB) * 100);
  });

  const verdict = computed(() => {
    const value = record.value;
    if (!value) return "";
    const quality = isBetter.value
      ? t("page.bayesianCompare.better", {}, { default: "лучше" })
      : t("page.bayesianCompare.worse", {}, { default: "хуже" });
    return t(
      "page.bayesianCompare.verdict",
      { target: value.targetValueOne, quality, percent: confidencePercent.value },
      {
        default: "Кампания {target} статистически {quality} с вероятностью {percent}%",
      },
    );
  });

  // Rate metrics show percentage points (×100); value-per-unit metrics show the
  // raw value.
  function formatStat(value: number): string {
    return isRateMetric.value ? (value * 100).toFixed(2) : value.toFixed(3);
  }

  const summaryRows = computed(() => {
    const value = stats.value;
    return [
      {
        label: t("page.bayesianCompare.expectedDiff", {}, { default: "Ожидаемая разница" }),
        value: value ? formatStat(value.expectedDiff) : "—",
      },
      {
        label: t("page.bayesianCompare.credibleInterval", {}, { default: "95% credible interval" }),
        value: value ? `[${formatStat(value.ci[0])}, ${formatStat(value.ci[1])}]` : "—",
      },
      {
        label: t("page.bayesianCompare.sampleSize", {}, { default: "Размер выборки" }),
        value: value ? `A: ${value.sizeA}, B: ${value.sizeB}` : "—",
      },
    ];
  });

  // --- Chart --------------------------------------------------------------
  type DensityPoint = { x: number; a: number; b: number };
  const chartData = computed(() => stats.value?.density ?? []);
  const chartX = (d: DensityPoint) => (isRateMetric.value ? d.x * 100 : d.x);
  const chartYA = (d: DensityPoint) => d.a;
  const chartYB = (d: DensityPoint) => d.b;
  const chartY = [chartYA, chartYB];

  const metricSelectOptions = computed(() =>
    METRIC_OPTIONS.map((option) => ({ label: option.label, value: option.key })),
  );

  // Crosshair tooltip — a dark panel showing the x value and both densities.
  function tooltipTemplate(d: DensityPoint): string {
    const row = (label: string, value: string, color?: string) =>
      `<div style="display:flex;justify-content:space-between;gap:16px;line-height:1.5;"><span style="${
        color ? `color:${color};` : "opacity:.6;"
      }">${label}</span><span>${value}</span></div>`;
    return `<div style="display:flex;flex-direction:column;gap:2px;font-size:12px;">${row(
      "x",
      isRateMetric.value ? (d.x * 100).toFixed(2) : d.x.toFixed(3),
    )}${row("A", d.a.toFixed(3), COLOR_A)}${row("B", d.b.toFixed(3), COLOR_B)}</div>`;
  }
</script>

<template>
  <div :class="$style.base">
    <section :class="$style.header">
      <h1 :class="$style.title">
        {{ $t("page.bayesianCompare.title", {}, { default: "Байесовский анализ" }) }}
      </h1>
      <span :class="$style.subtitle">{{ subtitle }}</span>
    </section>

    <section :class="$style.metrics">
      <VSelect
        v-model="metric"
        :options="metricSelectOptions"
        :class="$style.metricSelect"
        :clear="false"
      />
    </section>

    <section :class="$style.probCard">
      <span :class="$style.probLabel">{{
        $t("page.bayesianCompare.probLabel", {}, { default: "P(A > B)" })
      }}</span>
      <div :class="$style.probMain">
        <span :class="$style.probValue">{{ probPercent }}%</span>
        <span :class="[$style.badge, isBetter ? $style.badgeUp : $style.badgeDown]">
          {{ (isBetter ? "▲ " : "▼ ") + confidencePercent + "%" }}
        </span>
      </div>
      <span :class="$style.verdict">{{ verdict }}</span>
    </section>

    <section :class="$style.chartCard">
      <div :class="$style.legend">
        <span :class="$style.legendItem">
          <span :class="$style.dot" :style="{ backgroundColor: COLOR_A }" />
          {{ record?.targetValueOne }}
        </span>
        <span :class="$style.legendItem">
          <span :class="$style.dot" :style="{ backgroundColor: COLOR_B }" />
          {{ versusLabel }}
        </span>
      </div>
      <div :class="$style.chart">
        <svg :class="$style.gradients" aria-hidden="true">
          <defs>
            <linearGradient id="bayesGradA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" :stop-color="COLOR_A" stop-opacity="0.35" />
              <stop offset="100%" :stop-color="COLOR_A" stop-opacity="0" />
            </linearGradient>
            <linearGradient id="bayesGradB" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" :stop-color="COLOR_B" stop-opacity="0.35" />
              <stop offset="100%" :stop-color="COLOR_B" stop-opacity="0" />
            </linearGradient>
          </defs>
        </svg>
        <VisXYContainer :data="chartData" :height="220">
          <VisArea :x="chartX" :y="chartYA" color="url(#bayesGradA)" />
          <VisArea :x="chartX" :y="chartYB" color="url(#bayesGradB)" />
          <VisLine :x="chartX" :y="chartYA" :color="COLOR_A" :line-width="2" />
          <VisLine :x="chartX" :y="chartYB" :color="COLOR_B" :line-width="2" />
          <VisAxis type="x" :num-ticks="5" :grid-line="false" />
          <VisCrosshair
            :x="chartX"
            :y="chartY"
            :color="[COLOR_A, COLOR_B]"
            :template="tooltipTemplate"
          />
          <VisTooltip />
        </VisXYContainer>
      </div>
    </section>

    <section :class="$style.summaryWrapper">
      <Card :class="$style.summary">
        <div v-for="row in summaryRows" :key="row.label" :class="$style.summaryRow">
          <span :class="$style.summaryLabel">{{ row.label }}</span>
          <span :class="$style.summaryValue">{{ isLoading ? "—" : row.value }}</span>
        </div>
      </Card>

      <Card :class="$style.info">
        <button type="button" :class="$style.infoHeader" @click="infoOpen = !infoOpen">
          <span :class="$style.infoTitle">
            {{ $t("page.bayesianCompare.howItWorks.title", {}, { default: "Как это работает" }) }}
          </span>
          <ChevronDown v-if="infoOpen" :size="18" :class="$style.infoChevron" />
          <ChevronRight v-else :size="18" :class="$style.infoChevron" />
        </button>
        <div v-if="infoOpen" :class="$style.infoContent">
          <p :class="$style.infoText">
            {{
              $t(
                "page.bayesianCompare.howItWorks.p1",
                {},
                {
                  default:
                    "Постериорное распределение — это распределение возможных значений метрики после учёта данных. Чем оно уже, тем выше уверенность в оценке.",
                },
              )
            }}
          </p>
          <p :class="$style.infoText">
            <strong>{{
              $t("page.bayesianCompare.howItWorks.p2Term", {}, { default: "P(A > B)" })
            }}</strong
            >{{
              $t(
                "page.bayesianCompare.howItWorks.p2",
                {},
                {
                  default:
                    "— вероятность того, что истинное значение метрики у кампании A выше, чем у B. Это не «насколько лучше», а «насколько мы уверены, что лучше».",
                },
              )
            }}
          </p>
          <p :class="$style.infoText">
            <strong>{{
              $t("page.bayesianCompare.howItWorks.p3Term", {}, { default: "95% credible interval" })
            }}</strong
            >{{
              $t(
                "page.bayesianCompare.howItWorks.p3",
                {},
                {
                  default:
                    " — интервал, в который с вероятностью 95% попадает ожидаемая разница A − B.",
                },
              )
            }}
          </p>
        </div>
      </Card>
    </section>
  </div>
</template>

<style lang="scss" module>
  $pagePadding: 20px;
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 16px 0;

    overflow: auto;
    overscroll-behavior: contain;

    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    // Flex-item spacer that clears the fixed BottomNavigation on every engine
    // (matches StatisticsPage).
    &::after {
      content: "";
      flex-shrink: 0;
      height: calc(57px + 32px + env(safe-area-inset-bottom, 0px));
    }
  }

  .header {
    padding: 0 $pagePadding;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title {
    margin: 0;

    font-weight: 600;
    font-size: 1.25rem;
    line-height: 1.75rem;
  }

  .subtitle {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .metrics {
    padding: 0 $pagePadding;
  }

  .metricSelect {
    width: 100%;
    max-width: 100%;
  }

  // Probability card.
  .probCard {
    padding: 1rem;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    text-align: center;
  }

  .probLabel {
    color: var(--muted-foreground);

    font-size: 0.875rem;
    letter-spacing: 0.03em;
  }

  .probMain {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .probValue {
    font-weight: 700;
    font-size: 3rem;
    line-height: 1;
    letter-spacing: -0.02em;

    color: var(--foreground);
  }

  .badge {
    padding: 4px 10px;

    display: inline-flex;
    align-items: center;
    gap: 4px;

    border-radius: 9999px;

    font-size: 0.8125rem;
    font-weight: 600;
  }

  .badgeUp {
    color: var(--success);
    background-color: color-mix(in oklch, var(--success) 15%, transparent);
  }

  .badgeDown {
    color: var(--destructive);
    background-color: color-mix(in oklch, var(--destructive) 15%, transparent);
  }

  .verdict {
    max-width: 28rem;

    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  // Chart card.
  .chartCard {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .legend {
    padding: 0 $pagePadding;

    display: flex;
    gap: 16px;
  }

  .legendItem {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    color: var(--muted-foreground);
    font-size: 0.8125rem;
  }

  .dot {
    width: 10px;
    height: 10px;

    border-radius: 9999px;
  }

  .chart {
    position: relative;
    width: 100%;

    --vis-axis-tick-color: var(--border);
    --vis-axis-tick-label-color: var(--muted-foreground);
    --vis-axis-domain-color: var(--border);

    // Crosshair tooltip styling.
    --vis-tooltip-background-color: rgba(20, 20, 20, 0.9);
    --vis-tooltip-text-color: #fff;
    --vis-tooltip-border-color: rgba(255, 255, 255, 0.1);
    --vis-tooltip-padding: 8px 12px;
    --vis-tooltip-border-radius: 8px;
  }

  // Off-screen holder for the line/area gradient defs.
  .gradients {
    position: absolute;
    width: 0;
    height: 0;
  }

  .summaryWrapper {
    padding: 0 $pagePadding;

    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .summary {
    padding: 1rem;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .summaryRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .summaryLabel {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .summaryValue {
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.25rem;

    color: var(--foreground);
  }

  // Expandable "Как это работает" card.
  .infoHeader {
    width: 100%;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;
    font-family: inherit;
  }

  .infoTitle {
    font-weight: 600;
    font-size: 0.9375rem;
    line-height: 1.5rem;
  }

  .infoChevron {
    flex-shrink: 0;
    color: var(--muted-foreground);
  }

  .infoContent {
    margin-top: 12px;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .infoText {
    margin: 0;

    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.45;

    strong {
      color: var(--foreground);
      font-weight: 600;
    }
  }
</style>
